import React from 'react';
import { Book, BookOpen, Check, FileText, HelpCircle, Info, Music, Settings, Wand2, Sparkles, Compass } from 'lucide-react';
import { APP_VERSION } from '../version';
import SettingsPanel from './SettingsPanel';

/** Left navigation (Bible / Songs / AI Tools) with the Settings, Bible version and Help flyouts. */
const Sidebar = ({
  activeMenu,
  activeView,
  dm,
  isListening,
  setActiveMenu,
  setActiveView,
  setHelpModal,
  hasUnseenRelease,
  onStartTour,
  setSettings,
  settings,
  sidebarOpen,
  sidebarRef,
  t,
  updateSettings,
}) => {
  return (
    <aside
      ref={sidebarRef}
      className={`flex flex-col border-r shadow-xl transition-all duration-200 z-40 shrink-0 ${t.sidebarBg} ${
        sidebarOpen ? 'w-64 absolute inset-y-0 left-0 lg:static' : 'w-16'
      }`}
    >
      {/* View navigation */}
      <nav className="flex flex-col gap-1.5 p-3">
        {[
          { key: 'bible', label: 'Bible', Icon: Book },
          { key: 'songs', label: 'Songs', Icon: Music },
          { key: 'ai', label: 'AI Tools', Icon: Wand2 },
        ].map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setActiveView(key)}
            title={label}
            className={`relative flex items-center gap-3 pl-3 pr-3 py-3 rounded-lg text-sm font-semibold transition-all border-l-4 ${
              activeView === key
                ? (dm ? 'bg-blue-600/20 text-blue-400 border-brand-500' : 'bg-blue-50 text-blue-700 border-brand-500')
                : `${t.dropdownItem} border-transparent`
            }`}
          >
            <Icon className="w-5 h-5 shrink-0" />
            {sidebarOpen && <span className="truncate">{label}</span>}
            {key === 'bible' && isListening && activeView !== 'bible' && (
              <span className="absolute top-1.5 left-7 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            )}
          </button>
        ))}
      </nav>

      <div className={`border-t mx-3 ${t.divider}`} />

      {/* Tools */}
      <div className="flex flex-col gap-1.5 p-3">
      {/* Settings */}
      <div className="relative">
        <button
          onClick={() => setActiveMenu(activeMenu === 'settings' ? null : 'settings')}
          title="Display Settings"
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
            activeMenu === 'settings' ? (dm ? 'bg-slate-700 text-slate-50' : 'bg-gray-100 text-gray-900') : t.dropdownItem
          }`}
        >
          <Settings className="w-5 h-5 shrink-0" />
          {sidebarOpen && <span className="truncate">Settings</span>}
        </button>
        {activeMenu === 'settings' && (
          <SettingsPanel
            settings={settings}
            setSettings={setSettings}
            updateSettings={updateSettings}
            onClose={() => setActiveMenu(null)}
            dm={dm}
            t={t}
          />
        )}
      </div>

      {/* Bible Version */}
      <div className="relative">
        <button
          onClick={() => setActiveMenu(activeMenu === 'bibleVersion' ? null : 'bibleVersion')}
          title={`Bible Version: ${settings.bibleVersion}`}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
            activeMenu === 'bibleVersion' ? (dm ? 'bg-slate-700 text-slate-50' : 'bg-gray-100 text-gray-900') : t.dropdownItem
          }`}
        >
          <BookOpen className="w-5 h-5 shrink-0" />
          {sidebarOpen && <span className="flex-1 text-left truncate">Bible Version</span>}
          {sidebarOpen && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-green-900/30 text-green-400 font-mono">{settings.bibleVersion}</span>
          )}
        </button>
        {activeMenu === 'bibleVersion' && (
          <div className={`fixed ${sidebarOpen ? 'left-[16.5rem]' : 'left-[4.25rem]'} top-4 bottom-4 w-72 max-w-[calc(100vw-1rem)] max-h-[calc(100dvh-2rem)] border rounded-lg shadow-xl z-50 overflow-y-auto ${t.dropdownBg}`}>
            <div className={`px-4 py-2 text-xs font-bold text-green-400 bg-green-900/20 border-b sticky top-0 flex items-center gap-1.5 ${t.dropdownDiv}`}>
              <BookOpen className="w-3.5 h-3.5" /> Bible Versions
            </div>
            {['KJV','ASV','WEB','BBE','DARBY','YLT','NIV','NIVUK','NASB','AMP','TPT'].map(v => (
              <button key={v}
                onClick={() => { setSettings({ ...settings, bibleVersion: v }); setActiveMenu(null); }}
                className={`w-full text-left px-4 py-2 text-sm transition-all flex items-center justify-between ${settings.bibleVersion === v ? 'text-green-400 bg-green-900/20' : t.dropdownItem}`}
              >
                <span>{{KJV:'KJV - King James Version',ASV:'ASV - American Standard',WEB:'WEB - World English Bible',BBE:'BBE - Bible in Basic English',DARBY:"DARBY - Darby Translation",YLT:"YLT - Young's Literal",NIV:'NIV - New International',NIVUK:'NIVUK - New International (UK)',NASB:'NASB - New American Standard',AMP:'AMP - Amplified Bible',TPT:'TPT - The Passion Translation'}[v]}</span>
                {settings.bibleVersion === v && <Check className="w-4 h-4" />}
              </button>
            ))}
          </div>
        )}
      </div>

      </div>

      {/* Help (bottom of sidebar) */}
      <div className="mt-auto p-2">
      <div className="relative">
        <button
          onClick={() => setActiveMenu(activeMenu === 'help' ? null : 'help')}
          title="Help"
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
            activeMenu === 'help' ? (dm ? 'bg-slate-700 text-slate-50' : 'bg-gray-100 text-gray-900') : t.dropdownItem
          }`}
        >
          <HelpCircle className="w-5 h-5 shrink-0" />
          {sidebarOpen && <span className="truncate">Help</span>}
          {hasUnseenRelease && <span className="ml-auto w-2 h-2 rounded-full bg-emerald-500" aria-label="New release notes" />}
        </button>
        {activeMenu === 'help' && (
          <div className={`absolute left-full bottom-0 ml-1 w-64 max-h-[calc(100dvh-2rem)] overflow-y-auto border rounded-lg shadow-xl z-50 ${t.dropdownBg}`}>
            <button
              onClick={() => { onStartTour(); setActiveMenu(null); }}
              className={`w-full text-left px-4 py-2 text-sm transition-all flex items-center gap-2 ${t.dropdownItem}`}
            >
              <Compass className="w-4 h-4" />
              Take a tour
            </button>
            <button
              onClick={() => { setHelpModal('changelog'); setActiveMenu(null); }}
              className={`w-full text-left px-4 py-2 text-sm transition-all flex items-center gap-2 ${t.dropdownItem}`}
            >
              <Sparkles className="w-4 h-4" />
              What's new
              {hasUnseenRelease && <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-600 text-white">New</span>}
            </button>
            <button
              onClick={() => { setHelpModal('about'); setActiveMenu(null); }}
              className={`w-full text-left px-4 py-2 text-sm transition-all flex items-center gap-2 ${t.dropdownItem}`}
            >
              <Info className="w-4 h-4" />
              About SleekHear
            </button>
            <button
              onClick={() => { setHelpModal('guide'); setActiveMenu(null); }}
              className={`w-full text-left px-4 py-2 text-sm transition-all flex items-center gap-2 ${t.dropdownItem}`}
            >
              <HelpCircle className="w-4 h-4" />
              Quick Start Guide
            </button>
            <button
              onClick={() => { setHelpModal('shortcuts'); setActiveMenu(null); }}
              className={`w-full text-left px-4 py-2 text-sm transition-all flex items-center gap-2 ${t.dropdownItem}`}
            >
              <FileText className="w-4 h-4" />
              Keyboard Shortcuts
            </button>
            <div className={`border-t my-1 ${t.dropdownDiv}`}></div>
            <div className={`px-4 py-2 text-xs ${t.textSecondary}`}>
              Version {APP_VERSION}
            </div>
          </div>
        )}
      </div>
      </div>
    </aside>
  );
};

export default Sidebar;
